import { useCallback, useEffect, useRef, useState } from 'react'
import { OperatingRoomFilterDTO, OperatingRoomSummaryDTO } from '../../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import { getOperatingRoomSummary, isAccessDenied, isRequestCanceled, isSurgeonAccessDenied } from '../../services/OperatingRoomIndicatorsController'

/** `ready`: hay un resultado (puede estar actualizándose); `denied`: el perfil no tiene acceso a la pantalla. */
export type OperatingRoomSummaryStatus = 'loading' | 'ready' | 'error' | 'denied'

/** Qué se rechazó: la pantalla entera o solo los datos del cirujano (el filtro, sin la acción 46). */
export type OperatingRoomDeniedKind = 'screen' | 'surgeon'

/**
 * Resumen del período: se vuelve a pedir cada vez que cambian los filtros aplicados (período y «Más filtros»; no los
 * filtros de columna de las grillas). Cada consulta cancela la anterior, así que una respuesta vieja nunca pisa a una nueva.
 * Mientras consulta conserva el resultado anterior (`summary`) para que la pantalla lo muestre desenfocado; si la consulta
 * falla, el estado es `error` (nunca un resultado vacío) y «Reintentar» (`retry`) vuelve a pedir lo mismo.
 */
export default function useOperatingRoomSummary(applied: OperatingRoomFilterDTO) {
  const [summary, setSummary] = useState<OperatingRoomSummaryDTO | null>(null)
  const [status, setStatus] = useState<OperatingRoomSummaryStatus>('loading')
  const [deniedKind, setDeniedKind] = useState<OperatingRoomDeniedKind>('screen')
  const controllerRef = useRef<AbortController | null>(null)

  const run = useCallback(async (filters: OperatingRoomFilterDTO) => {
    controllerRef.current?.abort()
    const controller = new AbortController()
    controllerRef.current = controller
    setStatus('loading')
    try {
      const result = await getOperatingRoomSummary(filters, controller.signal)
      if (controller.signal.aborted) return
      setSummary(result)
      setStatus('ready')
    } catch (error: unknown) {
      if (controller.signal.aborted || isRequestCanceled(error)) return
      if (isAccessDenied(error)) setDeniedKind(isSurgeonAccessDenied(error) ? 'surgeon' : 'screen')
      setStatus(isAccessDenied(error) ? 'denied' : 'error')
    }
  }, [])

  useEffect(() => {
    void run(applied)
  }, [applied, run])

  // Al desmontar se cancela lo que esté en vuelo
  useEffect(() => () => controllerRef.current?.abort(), [])

  const retry = useCallback(() => void run(applied), [applied, run])

  return { summary, status, deniedKind, retry }
}
